'use client';

/**
 * Client-only collection of the visitor's browser environment.
 *
 * The pure formatters ({@link formatBrowserContextForMessage},
 * {@link formatBrowserContextBlocks}) live in `./browser-context-format` so
 * server-only code can import them without crossing this `'use client'`
 * boundary (SERVER_CLIENT_IMPORT_BOUNDARY). They are re-exported here so
 * existing client imports keep working.
 */

import type { BrowserContext } from './browser-context-format';

export type { BrowserContext } from './browser-context-format';
export {
  formatBrowserContextForMessage,
  formatBrowserContextBlocks,
} from './browser-context-format';

/**
 * Collects current browser environment data
 */
export function collectBrowserContext(): BrowserContext {
  if (typeof window === 'undefined') {
    // Return minimal context for SSR
    return {
      url: '',
      userAgent: '',
      timezone: '',
      language: '',
      screenResolution: '',
      viewportSize: '',
      platform: '',
      cookieEnabled: false,
    };
  }

  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    return {
      url: window.location.href,
      userAgent: navigator.userAgent,
      timezone,
      language: navigator.language,
      screenResolution: `${screenWidth} x ${screenHeight}`,
      viewportSize: `${viewportWidth} x ${viewportHeight}`,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
    };
  } catch (error) {
    console.error('Failed to collect browser context:', error);
    // Return partial context if collection fails
    return {
      url: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      timezone: '',
      language: typeof navigator !== 'undefined' ? navigator.language : '',
      screenResolution: '',
      viewportSize: '',
      platform: typeof navigator !== 'undefined' ? navigator.platform : '',
      cookieEnabled: typeof navigator !== 'undefined' ? navigator.cookieEnabled : false,
    };
  }
}
